import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import Counter from '@repo/ui/counter'
import DataTable from '@repo/ui/data-table'
import { FilterIcon, PrintIcon, StarIcon, UploadIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { exportEndOfJourneyReviewsSummary } from '../../../api/end-of-journey-reviews/summary/export-end-of-journey-reviews-summary'
import { getEndOfJourneyReviewsSummary } from '../../../api/end-of-journey-reviews/summary/get-end-of-journey-reviews-summary'
import {
  EndOfJourneyReviewsSummaryFilter,
  EndOfJourneyReviewsSummaryRequest,
  EndOfJourneyReviewsSummaryResponse
} from '../../../api/end-of-journey-reviews/summary/types'
import FilterDrawer from '../../../components/elements/drawers/filter-drawer'
import {
  DynamicDataTableSearchInput,
  DynamicDataTableTitle
} from '../../../components/elements/dynamic-table'
import UserNavBar from '../../../components/elements/nav-bar'
import { ReviewLoadError } from '../../../components/pages/ols/centers/center-tab-views/center-end-of-stay-appreciations/review-rating-pill'
import { useFilters } from '../../../lib/hooks/use-filters'
import useTranslate from '../../../lib/hooks/use-translate'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import EndOfStayAppreciationsSummaryFilter from './end-of-stay-appreciations-summary-filter'
import { getSummaryColumns } from './summary-columns'
import {
  buildSummaryRows,
  countSummaryFilters,
  getSummaryInitialFilters,
  openPrintWindow,
  writePrintWindow
} from './utils'

const noop = () => undefined

/** "Appréciations de fin de séjour": the youths' ratings of every centre, by country (card 139). */
const EndOfStayAppreciationsSummaryPage = () => {
  const { t } = useTranslate()
  const pageTitle = t('end.of.stay.appreciations.summary.page.title')
  const queryClient = useQueryClient()
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [countryOrder, setCountryOrder] = useState<'asc' | 'desc'>('asc')
  // Every country is collapsed when the page opens (07/10 2.9)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  // Centre rows ticked for the print / export (07/10 2.8)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [printData, setPrintData] = useState<EndOfJourneyReviewsSummaryResponse>()
  const printRef = useRef<HTMLDivElement>(null)
  const printWindowRef = useRef<Window | null>(null)

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<EndOfJourneyReviewsSummaryFilter>({ initialFilters: getSummaryInitialFilters() })

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  // The "Pays" toggle reloads the page: the backend sends the countries in that order
  const request: EndOfJourneyReviewsSummaryRequest = {
    search: search || undefined,
    sort: { country: countryOrder },
    filter: selectedFilter
  }

  const { data, isPending, isFetching, error } = useQuery({
    queryKey: ['end-of-journey-reviews-summary', request],
    queryFn: () => getEndOfJourneyReviewsSummary(request),
    placeholderData: keepPreviousData
  })

  // A new result drops the ticks of rows that are no longer listed
  useEffect(() => {
    if (!data) return
    const listed = new Set(data.countries.flatMap(({ centres }) => centres.map(({ id }) => id)))
    setSelectedIds((current) => new Set([...current].filter((id) => listed.has(id))))
  }, [data])

  // The ticked rows only, with their own totals, computed by the backend. No ids when every
  // listed row is ticked (or none): the request then lists them all, and its URL stays short
  const selectionRequest = (): EndOfJourneyReviewsSummaryRequest => {
    const allTicked = !!data && selectedIds.size === data.total
    return {
      ...request,
      filter: {
        ...selectedFilter,
        centerIds: selectedIds.size && !allTicked ? [...selectedIds] : undefined
      }
    }
  }

  const { mutate: exportExcel, isPending: isExporting } = useMutation({
    mutationFn: () => exportEndOfJourneyReviewsSummary(selectionRequest()),
    onError: showAxiosError
  })

  const { mutate: printTable, isPending: isPreparingPrint } = useMutation({
    mutationFn: () => {
      const input = selectionRequest()
      return queryClient.fetchQuery({
        queryKey: ['end-of-journey-reviews-summary', input],
        queryFn: () => getEndOfJourneyReviewsSummary(input)
      })
    },
    onSuccess: setPrintData,
    onError: (printError) => {
      printWindowRef.current?.close()
      printWindowRef.current = null
      showAxiosError(printError)
    }
  })

  // The window is opened in the click itself, otherwise the browser blocks it
  const onPrint = () => {
    const printWindow = openPrintWindow(
      pageTitle,
      t('end.of.stay.appreciations.summary.print.loading')
    )
    if (!printWindow) {
      toast.error(t('end.of.stay.appreciations.summary.print.blocked'))
      return
    }
    printWindowRef.current = printWindow
    printTable()
  }

  // Prints once the print table is rendered
  useEffect(() => {
    const printWindow = printWindowRef.current
    if (!printData || !printRef.current || !printWindow) return
    writePrintWindow(printWindow, printRef.current.innerHTML, pageTitle)
    printWindowRef.current = null
    setPrintData(undefined)
  }, [printData, pageTitle])

  const onSelect = (ids: number[], selected: boolean) =>
    setSelectedIds((current) => {
      const next = new Set(current)
      ids.forEach((id) => (selected ? next.add(id) : next.delete(id)))
      return next
    })

  const onToggleCountry = (countryKey: string) =>
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(countryKey)) next.delete(countryKey)
      else next.add(countryKey)
      return next
    })

  const rows = useMemo(
    () => (data ? buildSummaryRows(data, expanded, t) : []),
    [data, expanded, t]
  )
  const columns = useMemo(
    () =>
      data
        ? getSummaryColumns(data, {
            t,
            expanded,
            selectedIds,
            countryOrder,
            onToggleCountry,
            onSelect,
            onToggleCountryOrder: () =>
              setCountryOrder((order) => (order === 'asc' ? 'desc' : 'asc'))
          })
        : [],
    [data, expanded, selectedIds, countryOrder, t]
  )

  const printRows = useMemo(
    // Already sorted by the backend (the print request sends the "Pays" order, like the export)
    () => (printData ? buildSummaryRows(printData, new Set(), t, true) : []),
    [printData, t]
  )
  const printColumns = useMemo(
    () =>
      printData
        ? getSummaryColumns(printData, {
            t,
            readOnly: true,
            expanded: new Set(),
            selectedIds: new Set(),
            countryOrder,
            onToggleCountry: noop,
            onSelect: noop,
            onToggleCountryOrder: noop
          })
        : [],
    [printData, countryOrder, t]
  )

  const filtersLength = countSummaryFilters(selectedFilter)

  return (
    <div>
      <UserNavBar title={pageTitle} icon={StarIcon} />
      <div className="h-4" />
      <Box>
        <div className="flex justify-between items-center flex-wrap gap-2 py-2">
          <div className="flex items-center gap-4">
            <DynamicDataTableTitle>
              {t('end.of.stay.appreciations.summary.table.title')}
            </DynamicDataTableTitle>
            <Badge
              text={t('end.of.stay.appreciations.summary.total', { total: data?.total ?? 0 })}
              variant="warning"
              size="medium"
            />
          </div>
          <DynamicDataTableSearchInput
            value={searchInput}
            headerType="full"
            onChange={setSearchInput}
          />
        </div>
        <div className="flex justify-between items-center gap-2 mt-4">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              icon={PrintIcon}
              label={t('end.of.stay.appreciations.summary.print')}
              loading={isPreparingPrint}
              disabled={!data?.total || isFetching}
              onClick={onPrint}
            />
            <Button
              type="button"
              variant="secondary"
              icon={UploadIcon}
              label={t('end.of.stay.appreciations.summary.export')}
              loading={isExporting}
              disabled={!data?.total || isFetching}
              onClick={() => exportExcel()}
            />
          </div>
          <Button
            type="button"
            variant={filtersLength ? 'highlighted' : 'secondary'}
            icon={FilterIcon}
            label={t('end.of.stay.appreciations.summary.filter')}
            onClick={() => setIsFilterOpen(true)}
          >
            <Counter number={filtersLength} />
          </Button>
        </div>
        <div className="h-4" />
        {isPending ? (
          <Loader center />
        ) : error ? (
          <ReviewLoadError error={error} />
        ) : (
          <DataTable
            manualSorting={false}
            columns={columns}
            data={rows}
            rowCount={rows.length}
            paginated={false}
            isLoading={isFetching}
          />
        )}
      </Box>

      {/* Rendered only to be copied into the print window */}
      <div ref={printRef} className="hidden">
        {printData && (
          <DataTable
            manualSorting={false}
            columns={printColumns}
            data={printRows}
            rowCount={printRows.length}
            paginated={false}
          />
        )}
      </div>

      <FilterDrawer
        isOpen={isFilterOpen}
        selectedFilter={selectedFilter}
        isLoading={isFetching}
        applyLabel={t('button.modal.validate')}
        onReset={resetFilters}
        onFilterChange={onFilterChange}
        onClose={() => setIsFilterOpen(false)}
        onApply={() => onSelectedFilterChange({ ...filter })}
      >
        <EndOfStayAppreciationsSummaryFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}

export default EndOfStayAppreciationsSummaryPage
